// UserProfileHero — Cover + Avatar + Stats + Action buttons
import { Bot, Edit3, Hash, Heart, MessageSquare, Phone, Star, UserCheck, UserPlus, Video, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { API_URL, apiFetch } from '../config/api';
import { useAuth } from '../contexts/AuthContext';

interface ProfileUser {
  id: number; username: string; display_name: string; bio?: string; role?: string
  level?: number; total_xp?: number; followers_count?: number; following_count?: number
  posts_count?: number; agents_count?: number; avatar_url?: string; cover_color?: string
  phone?: string
}

interface Props {
  profile: ProfileUser
  agentsCount: number
  isOwn: boolean
  onEditProfile: () => void
  onRefresh: () => void
}

type FriendStatus = 'none' | 'pending' | 'accepted'

export default function UserProfileHero({ profile, agentsCount, isOwn, onEditProfile }: Props) {
  const { user: currentUser, isAuthenticated } = useAuth()
  const [following, setFollowing] = useState(false)
  const [friendStatus, setFriendStatus] = useState<FriendStatus>('none')
  const [searchQ, setSearchQ] = useState('')
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [searchOpen, setSearchOpen] = useState(false)
  const searchRef = useRef<HTMLDivElement>(null)

  const displayName = profile.display_name || profile.username || 'User'
  const coverGradient = profile.cover_color
    ? `linear-gradient(135deg, ${profile.cover_color}55, ${profile.cover_color}99)`
    : 'linear-gradient(135deg, #667eea55, #764ba299)'

  // Check follow/friend status
  useEffect(() => {
    if (!isAuthenticated || isOwn || !profile.id) return
    apiFetch(`${API_URL}/api/social/follow/status?target_id=${profile.id}`)
      .then(r => r.ok ? r.json() : null)
      .then((d: any) => { if (d) setFollowing(d.following ?? false) })
      .catch(() => {})
    apiFetch(`${API_URL}/api/friends/status?target_id=${profile.id}`)
      .then(r => r.ok ? r.json() : null)
      .then((d: any) => { if (d) setFriendStatus(d.status ?? 'none') })
      .catch(() => {})
  }, [isAuthenticated, isOwn, profile.id])

  // User search
  useEffect(() => {
    if (!searchQ.trim()) { setSearchResults([]); return }
    const t = setTimeout(() => {
      apiFetch(`${API_URL}/api/users/search?q=${encodeURIComponent(searchQ)}`)
        .then(r => r.ok ? r.json() : null)
        .then((d: any) => setSearchResults(d?.users ?? []))
        .catch(() => {})
    }, 300)
    return () => clearTimeout(t)
  }, [searchQ])

  // Close search on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) setSearchOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleFollow = async () => {
    if (!isAuthenticated) return
    await apiFetch(`${API_URL}/api/social/follow`, {
      method: 'POST',
      body: JSON.stringify({ target_id: profile.id, action: following ? 'unfollow' : 'follow' })
    })
    setFollowing(!following)
  }

  const handleAddFriend = async () => {
    if (!isAuthenticated || friendStatus !== 'none') return
    await apiFetch(`${API_URL}/api/friends/request`, {
      method: 'POST',
      body: JSON.stringify({ target_id: profile.id })
    })
    setFriendStatus('pending')
  }

  const openSideChat = () => {
    window.dispatchEvent(new CustomEvent('runai:open-sidechat', { detail: { userId: profile.id, username: profile.username, displayName } }))
  }

  const openPhone = () => window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'run-phone' }))
  const openMeet = () => window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'run-meet' }))

  const stats = [
    { val: profile.posts_count ?? 0, label: 'Posts', icon: <Hash size={12} /> },
    { val: profile.followers_count ?? 0, label: 'Followers', icon: <Heart size={12} /> },
    { val: profile.following_count ?? 0, label: 'Following', icon: <UserCheck size={12} /> },
    { val: profile.total_xp ?? 0, label: 'XP', icon: <Star size={12} /> },
    { val: profile.level ?? 1, label: 'Level', icon: <Star size={12} /> },
    { val: agentsCount, label: 'Agents', icon: <Bot size={12} /> },
  ]

  return (
    <div style={{ borderRadius: 16, overflow: 'visible', border: '1px solid var(--border-color)', background: 'var(--card-bg)' }}>
      {/* Cover */}
      <div style={{ height: 200, background: coverGradient, borderRadius: '16px 16px 0 0', position: 'relative' }}>
        {/* Search bar */}
        <div ref={searchRef} style={{ position: 'absolute', top: 14, left: 14, zIndex: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(10px)', borderRadius: 8, padding: '6px 10px', border: '1px solid rgba(255,255,255,0.12)' }}>
            <input
              value={searchQ}
              onChange={e => { setSearchQ(e.target.value); setSearchOpen(true) }}
              onFocus={() => setSearchOpen(true)}
              placeholder="Find users..."
              style={{ background: 'transparent', border: 'none', outline: 'none', color: '#fff', fontSize: 12, width: 160, '::placeholder': { color: 'rgba(255,255,255,0.5)' } } as React.CSSProperties}
            />
            {searchQ && <button onClick={() => { setSearchQ(''); setSearchResults([]) }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.6)', padding: 0, display: 'flex' }}><X size={12} /></button>}
          </div>
          {searchOpen && searchResults.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 4, background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 10, minWidth: 220, boxShadow: '0 8px 24px rgba(0,0,0,0.4)', zIndex: 100, overflow: 'hidden' }}>
              {searchResults.slice(0, 6).map((u: any) => (
                <a key={u.id} href={`/profile/${u.username}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', textDecoration: 'none', borderBottom: '1px solid var(--border-color)' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--body-bg)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg,#667eea,#764ba2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, fontWeight: 700, overflow: 'hidden', flexShrink: 0 }}>
                    {u.avatar_url ? <img src={u.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (u.display_name?.[0] ?? u.username?.[0] ?? '?').toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.display_name || u.username}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>@{u.username}</div>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>

        {isOwn && (
          <button onClick={onEditProfile} style={{ position: 'absolute', top: 14, right: 14, display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 8, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.15)', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            <Edit3 size={13} /> Edit Profile
          </button>
        )}
      </div>

      <div style={{ padding: '0 24px 20px', position: 'relative' }}>
        {/* Avatar */}
        <div style={{ width: 80, height: 80, borderRadius: '50%', background: profile.cover_color ? `linear-gradient(135deg,${profile.cover_color},#764ba2)` : 'linear-gradient(135deg,#667eea,#764ba2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 28, fontWeight: 800, border: '4px solid var(--card-bg)', marginTop: -40, marginBottom: 12, overflow: 'hidden', flexShrink: 0 }}>
          {profile.avatar_url ? <img src={profile.avatar_url} alt={displayName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : displayName[0]?.toUpperCase()}
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
              <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>{displayName}</h1>
              {profile.role === 'admin' && <span style={{ fontSize: 11, padding: '2px 8px', background: 'rgba(239,68,68,0.1)', color: '#ef4444', borderRadius: 6, fontWeight: 700 }}>Admin</span>}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8 }}>@{profile.username}</div>
            {profile.bio && <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, maxWidth: 480, margin: 0 }}>{profile.bio}</p>}
          </div>

          {/* Action buttons */}
          {!isOwn && isAuthenticated && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <ActionBtn onClick={handleFollow} active={following} icon={<UserCheck size={13} />} label={following ? 'Following' : 'Follow'} color="#667eea" />
              <ActionBtn onClick={handleAddFriend} active={friendStatus !== 'none'} icon={<UserPlus size={13} />} label={friendStatus === 'pending' ? 'Pending' : friendStatus === 'accepted' ? 'Friends' : 'Add Friend'} color="#10b981" disabled={friendStatus !== 'none'} />
              <ActionBtn onClick={openSideChat} icon={<MessageSquare size={13} />} label="Message" color="#6366f1" />
              {profile.phone && <ActionBtn onClick={openPhone} icon={<Phone size={13} />} label="Call" color="#f59e0b" />}
              <ActionBtn onClick={openMeet} icon={<Video size={13} />} label="Video" color="#ec4899" />
            </div>
          )}
        </div>

        {/* Stats bar */}
        <div style={{ display: 'flex', gap: 0, marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border-color)', flexWrap: 'wrap' }}>
          {stats.map((s, i) => (
            <div key={s.label} style={{ flex: '1 1 80px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, padding: '8px 4px', cursor: 'pointer', borderRight: i < stats.length - 1 ? '1px solid var(--border-color)' : 'none' }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--body-bg)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>{s.val.toLocaleString()}</div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 3 }}>{s.icon}{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function ActionBtn({ onClick, icon, label, color, active, disabled }: { onClick: () => void; icon: React.ReactNode; label: string; color: string; active?: boolean; disabled?: boolean }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 8, border: `1px solid ${active ? color : 'var(--border-color)'}`, background: active ? `${color}22` : 'var(--body-bg)', color: active ? color : 'var(--text-secondary)', fontSize: 12, fontWeight: 600, cursor: disabled ? 'default' : 'pointer', transition: 'all 0.15s', opacity: disabled ? 0.6 : 1 }}>
      {icon}{label}
    </button>
  )
}
